import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import SublocationMachinesForm from './SublocationMachinesForm';
import { FormikProvider, useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  setTotalCollected,
  setCollectionCollected,
  setTransformedSublocations,
  setTotalTokenCollected,
  setTotalTicketDispensed,
  setTotalPrizeDispensed,
  setFormikData,
  setTotalMedallionDispensed,
} from '../../../redux-slice/newcollection';
import {
  getCollectionCollected,
  getFieldName,
  getRevenueInLocalCurrency,
  getTotalCollected,
  getTotalMedallionDispensed,
  getTotalPrizeDispensed,
  getTotalTicketDispensed,
  getTotalTokenCollected,
  getTransformedData,
} from '../../../utils/collection-methods';
import { isArrayWithLength } from '../../../utils/common-methods';
import { FILTER_VALUES } from '../../../constants/FilterConstants';
import { DEFAULT_EXCHANGE_RATE } from '../../../constants/CurrencyConstants';
import { NoContent } from '../../../components/shared';
import { Box, Typography } from '@mui/material';
import SublocationNavigator from './SublocationNavigator';
import { FIELD_NAME, METER_TYPE } from '../../../constants/MeterTypeFieldMap';
import { useProgressiveRender } from '../../../hooks/useProgressiveRender';

const displayName = 'CollectionForm';
const CollectionForm = React.forwardRef(
  (
    {
      allSublocations,
      location,
      initialStateAndScheme,
      onConfirm,
      filterParams,
      readFromDraft,
      noSublocation,
      isSearch,
      turnOffDraft,
      onSublocationAssetsUpdate,
    },
    ref,
  ) => {
    const { initialState, scheme } = initialStateAndScheme;
    const dispatch = useDispatch();
    const { totalCollected, totalCollectedInUSD, summary, locationCurrency, intercardData } = useSelector(
      (state) => state.newCollection,
    );
    const { playCards } = useSelector((state) => state.playCard);
    const [sublocationWarnings, setSublocationWarnings] = useState({});
    const [assetWarningsBySublocation, setAssetWarningsBySublocation] = useState({});
    const initialIntercardDataRef = useRef(null);
    const hasDateChangedRef = useRef(false);

    const handleSublocationWarningChange = useCallback((sublocationId, hasWarning, assetWarnings = {}) => {
      setSublocationWarnings((prev) => {
        if (prev?.[sublocationId] === hasWarning) {
          return prev;
        }

        return { ...prev, [sublocationId]: hasWarning };
      });

      setAssetWarningsBySublocation((prev) => {
        const previousAssetWarnings = prev?.[sublocationId] || {};
        const nextAssetWarnings = assetWarnings || {};
        const hasSameWarnings =
          Object.keys(previousAssetWarnings).length === Object.keys(nextAssetWarnings).length &&
          Object.keys(nextAssetWarnings).every(
            (assetId) => previousAssetWarnings[assetId] === nextAssetWarnings[assetId],
          );

        if (hasSameWarnings) {
          return prev;
        }

        return { ...prev, [sublocationId]: nextAssetWarnings };
      });
    }, []);

    // Track initial intercard data to detect date changes
    useEffect(() => {
      if (intercardData && initialIntercardDataRef.current === null) {
        initialIntercardDataRef.current = JSON.stringify(intercardData);
      } else if (
        intercardData &&
        initialIntercardDataRef.current !== null &&
        initialIntercardDataRef.current !== JSON.stringify(intercardData)
      ) {
        hasDateChangedRef.current = true;
      }
    }, [intercardData]);

    const formik = useFormik({
      initialValues: initialState,
      validationSchema: scheme,
      enableReinitialize: true,
      onSubmit: () => {
        onConfirm();
      },
    });

    useEffect(() => {
      ref.current = formik;
      if (formik.values && Object.values(formik.values).length > 0) {
        let total = 0;
        let totalInUSD = 0;
        let totalTicketDispensed = 0;
        let totalPrizeDispensed = 0;
        let totalTokenCollected = 0;
        let totalMedallionDispensed = 0;
        const exchangeRate = locationCurrency?.exchangeRate || DEFAULT_EXCHANGE_RATE;
        for (const key in playCards) {
          // For Non-Standalone sublocations, credit-card playcard sales must not roll into the
          // submitted total; only cash playcard sales are counted.  Standalone locations
          // (calculateRent = true) keep the full adjustedPlaycardSales value.
          const sublocationForKey = location?.sublocations?.find((sub) => String(sub.id) === String(key));
          const isStandaloneForKey = sublocationForKey?.calculateRent ?? false;
          const adjustedPlaycardSales = isStandaloneForKey
            ? playCards[key]?.adjustedPlaycardSales || 0
            : (playCards[key]?.adjustedPlaycardSales || 0) - (playCards[key]?.netCreditCardTotal || 0);
          const adjustedPlaycardSalesInLocalCurrency = adjustedPlaycardSales
            ? getRevenueInLocalCurrency(adjustedPlaycardSales, exchangeRate)
            : 0;
          total += adjustedPlaycardSalesInLocalCurrency;
          totalInUSD += adjustedPlaycardSales;
        }

        if (formik.values && Object.values(formik.values).length > 0) {
          Object.entries(formik.values).map(([key, value]) => {
            const { total: newTotal, totalInUSD: newTotalInUSD } = getTotalCollected(
              total,
              totalInUSD,
              key,
              value,
              exchangeRate,
            );
            total = newTotal;
            totalInUSD = newTotalInUSD;
            totalTicketDispensed = getTotalTicketDispensed(totalTicketDispensed, key, value);
            totalPrizeDispensed = getTotalPrizeDispensed(totalPrizeDispensed, key, value);
            totalMedallionDispensed = getTotalMedallionDispensed(totalMedallionDispensed, key, value);
            totalTokenCollected = getTotalTokenCollected(totalTokenCollected, key, value);
          });
          if (totalCollected !== total || totalCollectedInUSD !== totalInUSD) {
            dispatch(setTotalCollected({ total, totalInUSD }));
          }
          if (summary.totalTokenCollected !== totalTokenCollected) {
            dispatch(setTotalTokenCollected(totalTokenCollected));
          }
          if (summary.totalTicketDispensed !== totalTicketDispensed) {
            dispatch(setTotalTicketDispensed(totalTicketDispensed));
          }
          if (summary.totalMedallionDispensed !== totalMedallionDispensed) {
            dispatch(setTotalMedallionDispensed(totalMedallionDispensed));
          }
          if (summary.totalPrizeDispensed !== totalPrizeDispensed) {
            dispatch(setTotalPrizeDispensed(totalPrizeDispensed));
          }
          if (Object.values(formik.values).some((el) => el !== '')) {
            const { transformedSublocations, formikData } = getTransformedData(formik.values, location.sublocations);
            dispatch(setTransformedSublocations(transformedSublocations));
            dispatch(setFormikData(formikData));
            dispatch(setCollectionCollected(getCollectionCollected(transformedSublocations)));
          }
        }
      }
    }, [formik.values, playCards, locationCurrency, location.sublocations]);

    const { values: formValues, setFieldValue } = formik;

    useEffect(() => {
      // Only update impulse reader values when date has changed, not on initial load
      if (!hasDateChangedRef.current) {
        return;
      }

      if (
        !isArrayWithLength(location?.sublocations) ||
        !formValues ||
        Object.keys(formValues).length === 0 ||
        !intercardData ||
        Object.keys(intercardData).length === 0
      ) {
        return;
      }

      location.sublocations.forEach((sublocation) => {
        const sublocationId = sublocation?.id;
        sublocation?.assets?.forEach((asset) => {
          const impulseMeters = asset?.assetMeters?.filter(
            (assetMeter) => assetMeter?.meter?.name === METER_TYPE.IMPULSE_READER,
          );

          if (!isArrayWithLength(impulseMeters)) {
            return;
          }

          const assetIntercardData = intercardData?.[asset?.id];
          const creditCardValue =
            assetIntercardData?.creditCard ?? assetIntercardData?.intercardData?.[0]?.creditCard ?? 0;

          impulseMeters.forEach((assetMeter, index) => {
            const fieldName = getFieldName(
              METER_TYPE.IMPULSE_READER,
              FIELD_NAME.GAME_REVENUE,
              sublocationId,
              asset?.id,
              assetMeter?.meterId,
              assetMeter?.id,
            );

            if (!fieldName) {
              return;
            }

            const targetValue = index === 0 ? Number(creditCardValue) || 0 : 0;
            const currentValue = Number(formValues?.[fieldName]) || 0;

            if (currentValue !== targetValue) {
              setFieldValue(fieldName, targetValue, false);
            }
          });
        });
      });
    }, [intercardData, location]);

    useEffect(() => {
      if (formik?.errors && Object.values(formik.errors).length > 0) {
        ref.current = formik;
      }
    }, [formik.errors]);

    const filteredSublocations = useMemo(() => {
      if (!location?.sublocations || location.sublocations.length === 0) {
        return [];
      }

      return location.sublocations.filter((value) => {
        if (isArrayWithLength(filterParams[FILTER_VALUES.COLLECTION_SUBLOCATION])) {
          const selectedSubLocationIds =
            filterParams[FILTER_VALUES.COLLECTION_SUBLOCATION]?.map((item) => item.id) || [];
          return selectedSubLocationIds.includes(value.id);
        }
        return true;
      });
    }, [location?.sublocations, filterParams]);

    // Use reusable progressive render hook
    const {
      visibleItems: visibleSublocations,
      renderedCount,
      totalCount,
      isFullyLoaded,
    } = useProgressiveRender(filteredSublocations);

    const visibleSublocationWarnings = useMemo(
      () =>
        Object.fromEntries(
          filteredSublocations.map((sublocation) => [sublocation.id, Boolean(sublocationWarnings[sublocation.id])]),
        ),
      [filteredSublocations, sublocationWarnings],
    );

    const visibleAssetWarnings = useMemo(
      () =>
        Object.fromEntries(
          filteredSublocations.map((sublocation) => [sublocation.id, assetWarningsBySublocation[sublocation.id] || {}]),
        ),
      [filteredSublocations, assetWarningsBySublocation],
    );

    if (noSublocation && isSearch) {
      return (
        <NoContent title="No Asset found" desc={<>No results found, please try changing the search criteria.</>} />
      );
    }

    if (location?.sublocations && location?.sublocations.length > 0) {
      return (
        <FormikProvider value={formik} validateOnChange={false} validateOnBlur={true}>
          {visibleSublocations.map((sublocation) => (
            <SublocationMachinesForm
              key={sublocation?.id}
              sublocation={sublocation}
              originalSublocation={allSublocations?.find((el) => el.id === sublocation?.id)}
              readFromDraft={readFromDraft}
              turnOffDraft={turnOffDraft}
              onSublocationAssetsUpdate={onSublocationAssetsUpdate}
              onWarningChange={handleSublocationWarningChange}
            />
          ))}
          {!isFullyLoaded && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Loading sublocations... ({renderedCount} of {totalCount})
              </Typography>
            </Box>
          )}
          <SublocationNavigator
            sublocations={filteredSublocations}
            sublocationWarnings={visibleSublocationWarnings}
            assetWarnings={visibleAssetWarnings}
            isLoading={!isFullyLoaded}
          />
        </FormikProvider>
      );
    }
  },
);

CollectionForm.displayName = displayName;
export default CollectionForm;
